import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Loader2,
  Landmark,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, ConfirmDialog } from '../../components/ui';
import { StatCard, Card, Button, buttonClass } from '../../design-system';
import { formatCurrency } from '../../utils/format';

interface ContaBancaria {
  id: number;
  nome: string;
  banco: string | null;
  agencia: string | null;
  conta: string | null;
  tipo: string;
  saldoInicial: number;
  saldoAtual: number;
  ativo: boolean;
  cor: string | null;
}

interface ContaFormData {
  nome: string;
  banco: string;
  agencia: string;
  conta: string;
  tipo: string;
  saldoInicial: number;
  cor: string;
}

const tipoLabels: Record<string, string> = {
  CORRENTE: 'Conta Corrente',
  POUPANCA: 'Poupanca',
  INVESTIMENTO: 'Investimento',
  CAIXA: 'Caixa',
  CARTAO: 'Cartao de Credito',
};

const cores = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

export function ContasBancarias() {
  const { showSuccess, showError, showApiError } = useNotification();

  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConta, setEditingConta] = useState<ContaBancaria | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [formData, setFormData] = useState<ContaFormData>({
    nome: '',
    banco: '',
    agencia: '',
    conta: '',
    tipo: 'CORRENTE',
    saldoInicial: 0,
    cor: '#3B82F6',
  });
  const [isSaving, setIsSaving] = useState(false);

  const fetchContas = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/financeiro/contas');
      setContas(response.data);
    } catch (error) {
      showApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchContas();
    return () => controller.abort();
  }, [fetchContas]);

  const handleOpenModal = (conta?: ContaBancaria) => {
    if (conta) {
      setEditingConta(conta);
      setFormData({
        nome: conta.nome,
        banco: conta.banco || '',
        agencia: conta.agencia || '',
        conta: conta.conta || '',
        tipo: conta.tipo,
        saldoInicial: conta.saldoInicial,
        cor: conta.cor || '#3B82F6',
      });
    } else {
      setEditingConta(null);
      setFormData({
        nome: '',
        banco: '',
        agencia: '',
        conta: '',
        tipo: 'CORRENTE',
        saldoInicial: 0,
        cor: '#3B82F6',
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      showError('Informe o nome da conta');
      return;
    }

    setIsSaving(true);
    try {
      if (editingConta) {
        await api.put(`/financeiro/contas/${editingConta.id}`, formData);
        showSuccess('Conta atualizada com sucesso');
      } else {
        await api.post('/financeiro/contas', formData);
        showSuccess('Conta criada com sucesso');
      }
      setShowModal(false);
      fetchContas();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/financeiro/contas/${id}`);
      showSuccess('Conta excluida com sucesso');
      fetchContas();
    } catch (error) {
      showApiError(error);
    }
    setConfirmDelete(null);
  };

  const totalSaldo = contas.reduce((acc, conta) => acc + (conta.saldoAtual || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Contas Bancarias" subtitle="Gerencie suas contas e saldos">
        <button onClick={() => handleOpenModal()} className={buttonClass('primary')}>
          <Plus className="w-4 h-4" />
          Nova Conta
        </button>
      </PageHeader>

      {/* Resumo */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Saldo Total"
          value={formatCurrency(totalSaldo)}
          icon={<Landmark className="h-[18px] w-[18px]" />}
          tone={totalSaldo >= 0 ? 'positive' : 'negative'}
          delay={0}
        />
      </div>

      {/* Lista de Contas */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : contas.length === 0 ? (
        <Card className="p-12 text-center">
          <Landmark className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Nenhuma conta cadastrada</p>
          <button
            onClick={() => handleOpenModal()}
            className={`mt-4 ${buttonClass('primary')}`}
          >
            Cadastrar primeira conta
          </button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contas.map((conta) => (
            <Card key={conta.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${conta.cor || '#3B82F6'}1f` }}
                  >
                    <Landmark className="w-5 h-5" style={{ color: conta.cor || '#3B82F6' }} />
                  </div>
                  <div>
                    <h3 className="font-bold tracking-tight text-slate-900">{conta.nome}</h3>
                    <p className="text-sm text-slate-400">{tipoLabels[conta.tipo] || conta.tipo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleOpenModal(conta)}
                    className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(conta.id)}
                    className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {(conta.banco || conta.agencia || conta.conta) && (
                <div className="text-sm text-slate-500 mb-4">
                  {conta.banco && <span>{conta.banco}</span>}
                  {conta.agencia && <span> | Ag: {conta.agencia}</span>}
                  {conta.conta && <span> | Cc: {conta.conta}</span>}
                </div>
              )}

              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-400 mb-1">Saldo Atual</p>
                <div className="flex items-center gap-2">
                  {conta.saldoAtual >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-rose-500" />
                  )}
                  <span className={`text-xl font-bold tabular-nums ${conta.saldoAtual >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(conta.saldoAtual)}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Cadastro/Edicao */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-bold tracking-tight text-slate-900">
                {editingConta ? 'Editar Conta' : 'Nova Conta'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Ex: Conta Principal"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="CORRENTE">Conta Corrente</option>
                  <option value="POUPANCA">Poupanca</option>
                  <option value="INVESTIMENTO">Investimento</option>
                  <option value="CAIXA">Caixa</option>
                  <option value="CARTAO">Cartao de Credito</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Banco</label>
                  <input
                    type="text"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Agencia</label>
                  <input
                    type="text"
                    value={formData.agencia}
                    onChange={(e) => setFormData({ ...formData, agencia: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Conta</label>
                  <input
                    type="text"
                    value={formData.conta}
                    onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="12345-6"
                  />
                </div>
              </div>

              {!editingConta && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Saldo Inicial</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.saldoInicial}
                    onChange={(e) => setFormData({ ...formData, saldoInicial: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cor</label>
                <div className="flex flex-wrap gap-2">
                  {cores.map((cor) => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setFormData({ ...formData, cor })}
                      className={`w-8 h-8 rounded-lg transition-all ${
                        formData.cor === cor ? 'ring-2 ring-offset-2 ring-primary-500' : ''
                      }`}
                      style={{ backgroundColor: cor }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        title="Excluir Conta"
        message="Tem certeza que deseja excluir esta conta? Esta acao nao pode ser desfeita."
        confirmText="Excluir"
        type="danger"
      />
    </div>
  );
}
